import React, { useEffect, useState } from 'react';
import TextField from 'studio/components/TextField';
import Typography from '@mui/material/Typography';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import { trackEvent } from 'utils/eventTracking';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import { paths } from 'studio/App/Admin/Admin';
import RoleChip from 'studio/components/Chip/RoleChip/RoleChip';
import Link from 'studio/components/Link';
import Button from '@mui/material/Button';
import { veezooUrls } from 'config/constants';
import SecurityIcon from '@mui/icons-material/Security';
import _ from 'lodash';
import { fetchAllRoles } from 'utils/userRoleUtils';

import { sx, useStyles } from './AccessControl.styles';

const AccessControl = ({ knowledgeGraph, user: { id: userId } }) => {
  useEffect(() => {
    trackEvent('Studio Access Control Opened');
  }, []);

  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();
  const { classes } = useStyles();
  // the user
  const [user, setUser] = useState(null);
  // all available roles
  const [allRoles, setAllRoles] = useState([]);
  // the IDs of the current roles the knowledge graph is restricted to
  const [roleIdsForKnowledgeGraph, setRoleIdsForKnowledgeGraph] = useState([]);
  // the IDs of the new roles the knowledge graph should be restricted to
  const [newRoleIdsForKnowledgeGraph, setNewRoleIdsForKnowledgeGraph] = useState([]);

  const knowledgeGraphId = knowledgeGraph.data.id;
  const hasChanges = JSON.stringify(roleIdsForKnowledgeGraph) !== JSON.stringify(newRoleIdsForKnowledgeGraph);

  const getUser = async id => {
    const response = await withLoader(services.getUser(id));
    if (response.success) {
      const user = response.data.data;
      setUser(user);
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error getting the user';
      errorMessage(message);
    }
  };

  const getAllRoles = async () => {
    const roles = await fetchAllRoles(withLoader, errorMessage);
    setAllRoles(roles);
  };

  const getRolesForKnowledgeGraph = async () => {
    const response = await withLoader(services.getRolesForKnowledgeGraph(knowledgeGraphId));
    if (response.success) {
      const roleIds = response.data.data;
      setRoleIdsForKnowledgeGraph(roleIds);
      setNewRoleIdsForKnowledgeGraph(roleIds);
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error getting the restricted roles';
      setRoleIdsForKnowledgeGraph([]);
      setNewRoleIdsForKnowledgeGraph([]);
      errorMessage(message);
    }
  };

  const updateRolesForKnowledgeGraph = async () => {
    const response = await withLoader(
      services.updateRolesForKnowledgeGraph(knowledgeGraphId, newRoleIdsForKnowledgeGraph)
    );
    if (response.success) {
      successMessage('Updated restricted roles successfully');
    } else {
      const message = response?.response?.data?.data?.message || 'There was an error updating the restricted roles';
      errorMessage(message);
    }
  };

  useEffect(() => {
    getAllRoles();
    getRolesForKnowledgeGraph();
  }, [knowledgeGraph]);

  useEffect(() => {
    getUser(userId);
  }, [userId]);

  const getRole = id => allRoles.find(role => role.id === id);

  return (
    <div className={classes.root}>
      <Typography variant="h2" gutterBottom>
        <SecurityIcon />
        Access Control
      </Typography>
      <Typography variant="body1" gutterBottom>
        Restrict access to <i>{knowledgeGraph.data.info.default.name}</i> to specific roles: only users who are assigned
        to at least one of these roles have access to the knowledge graph. If no roles are specified, the knowledge
        graph can be accessed by all users.
      </Typography>
      <Typography variant="body1" gutterBottom>
        The roles and their assigned users can be managed in Veezoo Admin on the{' '}
        <Link href={paths.roles} newWindow>
          Roles
        </Link>{' '}
        page.
        <br />
        For other access control options, please check our{' '}
        <Link href={veezooUrls.roleDocs} newWindow>
          documentation
        </Link>
        .
      </Typography>
      <Typography variant="h3" gutterBottom>
        Roles
      </Typography>
      <Autocomplete
        multiple
        value={newRoleIdsForKnowledgeGraph}
        options={allRoles.map(role => role.id)}
        getOptionLabel={roleId => getRole(roleId)?.name || roleId}
        isOptionEqualToValue={(optionRoleId, valueRoleId) => optionRoleId === valueRoleId}
        renderInput={params => (
          <TextField
            {...params}
            sx={sx.textField}
            size="small"
            label="Roles"
            description="Select the role(s) to restrict the knowledge graph to"
          />
        )}
        filterSelectedOptions
        noOptionsText={newRoleIdsForKnowledgeGraph.length ? 'No more roles available' : 'No roles available'}
        renderTags={(valueRoleIds, getTagProps) =>
          valueRoleIds.map((roleId, index) => {
            const role = getRole(roleId);
            return <RoleChip key={roleId} roleId={roleId} role={role} {...getTagProps({ index })} />;
          })
        }
        onChange={(event, newRoleIds) => {
          setNewRoleIdsForKnowledgeGraph(newRoleIds);
        }}
        disabled={loading}
      />

      {!newRoleIdsForKnowledgeGraph.length && (
        <Alert severity="info" sx={sx.alert}>
          All users
          {hasChanges ? ' will ' : ' '}
          have access to the knowledge graph
        </Alert>
      )}
      {hasChanges &&
        !!newRoleIdsForKnowledgeGraph.length &&
        !_.intersection(newRoleIdsForKnowledgeGraph, user?.roleIds).length && (
          <Alert severity="warning" sx={sx.alert}>
            You will lose access to the knowledge graph since your user is not assigned to any of the selected roles
          </Alert>
        )}

      <Button
        sx={sx.saveButton}
        type="submit"
        variant="contained"
        color="primary"
        // only show the button if there are changes
        disabled={!hasChanges}
        onClick={async () => {
          await updateRolesForKnowledgeGraph();
          await getRolesForKnowledgeGraph();
        }}
      >
        Save
      </Button>
    </div>
  );
};

export default AccessControl;
